import { useMemo } from "react";
import { useAgentAnalytics, useAgentTimeline } from "@/hooks/analytics/useAnalytics";
import { useConversations } from "@/hooks/conversation/useConversations";
import type { Conversation } from "@/types/conversation/conversation";
import type {
  DayCount,
  FeelingTone,
  GenieAnalysis,
  TopicShare,
} from "@/types/voice-vision";

/**
 * Plain-language analysis for one genie, computed from real platform analytics:
 *  - 7-day volume + trend  -> useAgentTimeline (this 7d window vs the prior 7d)
 *  - totals                -> useAgentAnalytics (conversationCount for this id)
 *  - topics / sentiment    -> useConversations([id], "30d") aggregated client-side
 *
 * Metrics with no honest source are flagged (hasSentiment / hasResolution) so the
 * tab can render purpose-built "not enough data yet" empties instead of inventing
 * numbers. Only the first 30-day page is read for topics/sentiment — a
 * representative sample; `sampleTruncated` notes when more pages exist.
 */

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Start-of-day for a given Date (local). */
const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Normalize a satisfaction score that may arrive as 0-5 or 0-100 onto 0-5. */
const toRating5 = (raw: number): number => {
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return raw > 5 ? (raw / 100) * 5 : raw;
};

const round1 = (n: number) => Math.round(n * 10) / 10;

const bucketFeeling = (avgScore: number, hasMixed: boolean): FeelingTone => {
  // avgScore is the mean overall_sentiment_score, roughly -1..1 (or 0..1).
  if (hasMixed) return "mixed";
  if (avgScore >= 0.55) return "happy";
  if (avgScore <= 0.35) return "unhappy";
  return "neutral";
};

interface ConvAggregate {
  analyzedCount: number;
  durationSeconds: number;
  topicCounts: Map<string, number>;
  sentimentScoreSum: number;
  sentimentReadings: number;
  positive: number;
  negative: number;
  ratingSum: number;
  ratingReadings: number;
  resolvable: number;
  escalated: number;
}

const aggregateConversations = (convs: Conversation[]): ConvAggregate => {
  const agg: ConvAggregate = {
    analyzedCount: 0,
    durationSeconds: 0,
    topicCounts: new Map(),
    sentimentScoreSum: 0,
    sentimentReadings: 0,
    positive: 0,
    negative: 0,
    ratingSum: 0,
    ratingReadings: 0,
    resolvable: 0,
    escalated: 0,
  };

  for (const conv of convs) {
    const details = conv.details || ({} as Conversation["details"]);
    const analysis = (details.analysis_result || {}) as Record<string, any>;

    if (typeof details.duration === "number" && details.duration > 0) {
      agg.durationSeconds += details.duration;
    }

    const hasAnalysis = analysis && Object.keys(analysis).length > 0;
    if (hasAnalysis) agg.analyzedCount += 1;

    // Topics: prefer details.topics, fall back to analysis main_topics.
    const topics: string[] =
      (Array.isArray(details.topics) && details.topics.length > 0
        ? details.topics
        : Array.isArray(analysis.main_topics)
          ? analysis.main_topics
          : []) || [];
    for (const t of topics) {
      const label = (t || "").trim();
      if (!label) continue;
      agg.topicCounts.set(label, (agg.topicCounts.get(label) || 0) + 1);
    }

    // Sentiment.
    const sentiment = analysis.sentiment_analysis || {};
    const sType: string | undefined = sentiment.overall_sentiment_type;
    const sScore = sentiment.overall_sentiment_score;
    if (typeof sScore === "number") {
      agg.sentimentScoreSum += sScore;
      agg.sentimentReadings += 1;
    }
    if (sType === "positive") agg.positive += 1;
    else if (sType === "negative") agg.negative += 1;

    // Rating from customer_satisfaction_score (normalize 0-5 or 0-100).
    const csat = sentiment.customer_satisfaction_score;
    if (typeof csat === "number") {
      const r = toRating5(csat);
      if (r > 0) {
        agg.ratingSum += r;
        agg.ratingReadings += 1;
      }
    }

    // Resolution: read from real escalation. Only analyzed convs are countable.
    if (hasAnalysis) {
      agg.resolvable += 1;
      const escalation = analysis.escalation;
      const escalated =
        escalation != null &&
        !(typeof escalation === "object" && Object.keys(escalation).length === 0);
      if (escalated) agg.escalated += 1;
    }
  }

  return agg;
};

const buildLast7Days = (
  timeline: { date: string; conversations: number }[] | undefined,
): DayCount[] => {
  const today = startOfDay(new Date());
  const byDate = new Map<string, number>();
  for (const p of timeline || []) {
    // Timeline dates are ISO-ish; key by local day.
    const key = startOfDay(new Date(p.date)).getTime();
    byDate.set(String(key), (byDate.get(String(key)) || 0) + (p.conversations || 0));
  }
  const days: DayCount[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today.getTime() - i * MS_PER_DAY);
    const key = String(d.getTime());
    days.push({ day: DAY_LABELS[d.getDay()], count: byDate.get(key) || 0 });
  }
  return days;
};

const sumWindow = (
  timeline: { date: string; conversations: number }[] | undefined,
  from: Date,
  to: Date,
): number => {
  let total = 0;
  for (const p of timeline || []) {
    const t = new Date(p.date).getTime();
    if (t >= from.getTime() && t < to.getTime()) total += p.conversations || 0;
  }
  return total;
};

const buildTopTopics = (counts: Map<string, number>): TopicShare[] => {
  const entries = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  const max = entries.length ? entries[0][1] : 0;
  if (max === 0) return [];
  // Share relative to the most common topic, so the busiest bar reads ~100%.
  return entries.map(([label, count]) => ({
    label,
    pct: Math.max(6, Math.round((count / max) * 100)),
  }));
};

const ratioNote = (resolvable: number, escalated: number): string => {
  if (resolvable === 0) return "We will know once a few conversations land.";
  const handled = resolvable - escalated;
  if (escalated === 0) {
    return handled === 1
      ? "Every conversation so far was sorted without a person."
      : `All ${handled} were sorted without a person.`;
  }
  const ratio = Math.max(1, Math.round(resolvable / escalated));
  return `Only 1 in ${ratio} needed a person.`;
};

export const useGenieAnalysis = (id: string) => {
  const now = useMemo(() => new Date(), []);
  // 7-day chart window + prior 7-day window for a real trend comparison.
  const startThis = useMemo(
    () => new Date(startOfDay(now).getTime() - 6 * MS_PER_DAY),
    [now],
  );
  const startPrev = useMemo(
    () => new Date(startThis.getTime() - 7 * MS_PER_DAY),
    [startThis],
  );
  const endTimeline = useMemo(
    () => new Date(startOfDay(now).getTime() + MS_PER_DAY),
    [now],
  );

  const timelineQ = useAgentTimeline(id, startPrev, endTimeline);
  const analyticsQ = useAgentAnalytics();
  const convsQ = useConversations(id ? [id] : [], "30d");

  const data = useMemo<GenieAnalysis | undefined>(() => {
    if (!id) return undefined;
    if (timelineQ.isLoading || analyticsQ.isLoading || convsQ.isLoading) {
      return undefined;
    }

    const timeline = timelineQ.data?.data;

    // Headline total: real monthly conversationCount for this genie.
    const me = (analyticsQ.data || []).find((a) => a.id === id);
    const handledThisMonth = me?.conversationCount ?? 0;

    // Trend: this 7d vs prior 7d from the timeline, when both windows compute.
    const thisWeek = sumWindow(timeline, startThis, endTimeline);
    const prevWeek = sumWindow(timeline, startPrev, startThis);
    let changePct = 0;
    if (prevWeek > 0) {
      changePct = Math.round(((thisWeek - prevWeek) / prevWeek) * 100);
    }

    const last7Days = buildLast7Days(timeline);

    // Topics / sentiment / resolution from the first 30d page (representative).
    const pages = convsQ.data?.pages || [];
    const convs: Conversation[] = pages.flatMap((p) => p.data);
    const sampleTruncated = (convsQ.hasNextPage ?? false) || pages.length === 0;
    const agg = aggregateConversations(convs);

    const topTopics = buildTopTopics(agg.topicCounts);

    // hoursSaved: clearly-derived estimate from summed real duration.
    const hoursSaved = Math.max(0, Math.round(agg.durationSeconds / 3600));

    // Sentiment / feeling.
    const hasSentiment =
      agg.sentimentReadings > 0 || agg.ratingReadings > 0;
    const avgScore =
      agg.sentimentReadings > 0
        ? agg.sentimentScoreSum / agg.sentimentReadings
        : 0;
    const mixed =
      agg.positive > 0 &&
      agg.negative > 0 &&
      Math.abs(agg.positive - agg.negative) <= Math.max(1, agg.positive * 0.4);
    const feeling: FeelingTone = hasSentiment
      ? bucketFeeling(avgScore, mixed)
      : "neutral";
    const rating =
      agg.ratingReadings > 0 ? round1(agg.ratingSum / agg.ratingReadings) : 0;

    // Resolution from real escalation data.
    const hasResolution = agg.resolvable > 0;
    const resolvedWithoutHuman = hasResolution
      ? Math.round(((agg.resolvable - agg.escalated) / agg.resolvable) * 100)
      : 0;
    const resolvedNote = ratioNote(agg.resolvable, agg.escalated);

    return {
      genieId: id,
      handledThisMonth,
      changePct,
      hoursSaved,
      last7Days,
      topTopics,
      resolvedWithoutHuman,
      resolvedNote,
      feeling,
      rating,
      hasSentiment,
      hasResolution,
      sampledConversations: convs.length,
      sampleTruncated,
    };
  }, [
    id,
    timelineQ.isLoading,
    timelineQ.data,
    analyticsQ.isLoading,
    analyticsQ.data,
    convsQ.isLoading,
    convsQ.data,
    convsQ.hasNextPage,
    startThis,
    startPrev,
    endTimeline,
  ]);

  const isLoading =
    !!id &&
    (timelineQ.isLoading || analyticsQ.isLoading || convsQ.isLoading);
  const isError = timelineQ.isError || analyticsQ.isError || convsQ.isError;

  return { data, isLoading, isError };
};
